import React from 'react';
import { language } from '../LanguageService';
import { Modal } from '../../components/Carbon';
import { getRandomId, iterableToArray } from '../../helpers/helpers';
import { Subject } from 'rxjs';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { tracking } from '../TrackingService';
export class AsperaModalFocusTrap extends React.Component {
    constructor() {
        super(...arguments);
        this.subManager = new SubManager();
        this.checkFocus = () => {
            const { id } = this.props;
            const modalWrap = document.querySelector(`#${id}`);
            const closeActions = document.querySelectorAll('.cds--modal-close');
            const captureFocus = () => {
                if (closeActions && closeActions.length) {
                    closeActions.forEach(button => {
                        button.blur();
                    });
                }
            };
            if (modalWrap) {
                modalWrap.ontransitionend = captureFocus;
                modalWrap.onmouseenter = captureFocus;
                const formInModal = document.querySelector(`#${id} input, #${id} textarea, #${id} .autocomplete-text-box-input`);
                if (formInModal && typeof formInModal.focus === 'function') {
                    setTimeout(() => {
                        formInModal.focus();
                    });
                }
            }
            else {
                new AsperaError('AsperaModalFocusTrap: Unable to find new modal for focus trap', { id });
            }
        };
    }
    componentDidMount() {
        this.checkFocus();
        this.subManager.setSubscription(modal.formFocusTrigger.subscribe({
            next: () => {
                this.checkFocus();
            },
            error: error => {
                new AsperaError('AsperaModalFocusTrap: Unable to watch for form focus trigger', error);
            },
        }));
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        return null;
    }
}
/**
 * Create a Carbon Modal to show on the DOM
 *
 * The root component should use this service to get the list of Modals to render
 * and subscribe to the update pusher.
 *
 * Modals open on top of each other, so the last modal to be added renders the highest.
 * For best practice avoid multiple modals.
 */
export class ModalService {
    constructor() {
        /** Subscription manager */
        this.subManager = new SubManager();
        /** Map of modals currently rendered on UI */
        this.modals = new Map();
        /** Map of subjects attached to a modal ID */
        this.modalSubjects = new Map();
        /** Map of status responses attached to a modal ID */
        this.modalStatus = new Map();
        /** Data propagation subscription */
        this.notifier = new Subject();
        /** System to set focus on a modal form item */
        this.formFocus = new Subject();
        /**
         * Remove a modal from the current modals
         *
         * @param id - the ID of the modal that was assigned on creation
         */
        this.remove = (id) => {
            var _a;
            this.modals.delete(id);
            this.notifier.next(true);
            // Carbon removes this class on willUnmount.  This adds it back of more modals are in the service.
            if (this.modals.size) {
                (_a = document.querySelector('body')) === null || _a === void 0 ? void 0 : _a.classList.add('cds--body--with-modal-open');
            }
        };
        /**
         * Remove all modal from the current modals (this is used primarily on error state on AppWrapper)
         * NOTE: normal consumers should not use this
         */
        this.removeAll = () => {
            this.modals.clear();
            this.notifier.next(true);
        };
    }
    /**
     * Updates or creates a modal stored in the session and notifies consumers to update state.
     *
     * @param id - the ID of the modal that was assigned
     * @param propsToUse - full modal props to use for final modal
     * @param modalBody - inner content of the modal
     */
    setModalContent(id, propsToUse, modalBody) {
        const modal = (React.createElement(Modal, Object.assign({}, propsToUse, { key: id, "data-auto": `modal-${propsToUse.modalHeading}` }),
            React.createElement(AsperaModalFocusTrap, { id: propsToUse.id }),
            modalBody));
        this.modals.set(id, modal);
        this.notifier.next(true);
    }
    /**
     * Create a modal to show on the DOM.  You must call prepare prior to create. That provides the ID and the callback for componentWillUnmount
     *
     * @param id - the id of the modal
     * @param modalProps - limited props that can used for AsperaModals. Most common defaults are applied to simplify this
     * @param modalBody - the body content of the modal @example `<MyModal modalClose={modalData.callback} prop1="test" />`
     * @param disableListener - observable that takes a boolean indicating if the modal primary action should be disabled or not; if undefined the modal is never disabled.  This will result in the modal starting off disabled, follow with a next(true) to start it off enabled.
     * @param disableOverflowProperty - indicate if the overflow should be turned off for this modal. WARNING: Only use this for very short modals. As this will allow the modal to break out on small height or too long of forms.
     *
     * @returns props to use
     */
    getPropsToUse(id, modalProps, modalBody, disableListener, disableOverflowProperty) {
        const propsToUse = {};
        let subId;
        propsToUse.open = true;
        propsToUse.modalHeading = modalProps.modalHeading;
        propsToUse.modalLabel = modalProps.modalLabel;
        propsToUse.passiveModal = modalProps.passiveModal;
        propsToUse.size = modalProps.size;
        propsToUse.shouldSubmitOnEnter = false; // BUG in carbon; where will submit even if Modal button is disabled... modalProps.shouldSubmitOnEnter;
        propsToUse.danger = modalProps.danger;
        propsToUse.preventCloseOnClickOutside = true;
        propsToUse.modalAriaLabel = language.get('modal.modalDescription');
        propsToUse['aria-label'] = language.get('modal.modalDescription');
        propsToUse.hasScrollingContent = modalProps.hasScrollingContent || true;
        propsToUse.id = `AsperaModal-${id}`;
        propsToUse.selectorPrimaryFocus = `#${id}`;
        propsToUse.primaryButtonText = modalProps.primaryButtonText || language.get('modal.submit');
        propsToUse.secondaryButtonText = modalProps.secondaryButtonText || language.get('modal.cancel');
        propsToUse.className = `aspera-modal-service ${modalProps.className || ''} ${disableOverflowProperty ? 'modal-overflow-escape' : ''} ${modalProps.hideSecondaryButton ? 'modal-no-secondary-button' : ''}`;
        const removeCleanly = () => {
            // Allow animation to play for closing
            propsToUse.open = false;
            this.setModalContent(id, propsToUse, modalBody);
            setTimeout(() => {
                if (subId)
                    this.subManager.unsubscribe(subId);
                this.remove(id);
            }, 250);
        };
        tracking.track('Viewed Modal Dialog', { successFlag: true, category: modalProps.modalHeading });
        propsToUse.onRequestClose = () => {
            tracking.track('Closed Modal Dialog', { successFlag: true, category: modalProps.modalHeading });
            this.modalStatus.set(id, { description: 'ModalService: Modal closed via X', secondaryClose: false, xClose: true, primaryClose: false });
            removeCleanly();
        };
        const onSecondarySubmit = () => {
            tracking.track('Closed Modal Dialog', { successFlag: true, category: modalProps.modalHeading });
            this.modalStatus.set(id, { description: 'ModalService: Modal closed via secondary button', secondaryClose: true, xClose: false, primaryClose: false });
            removeCleanly();
        };
        propsToUse.onSecondarySubmit = typeof modalProps.secondaryOnClick === 'function' ? (() => {
            if (!modalProps.secondaryOnClick)
                return;
            modalProps.secondaryOnClick(onSecondarySubmit);
        }) : onSecondarySubmit;
        const onPrimarySubmit = () => {
            tracking.track('Closed Modal Dialog', { successFlag: true, category: modalProps.modalHeading });
            this.modalStatus.set(id, { description: 'ModalService: Modal closed via primary button', secondaryClose: false, xClose: false, primaryClose: true });
            removeCleanly();
        };
        propsToUse.onRequestSubmit = typeof modalProps.primaryOnClick === 'function' ? (() => {
            if (!modalProps.primaryOnClick)
                return;
            modalProps.primaryOnClick(onPrimarySubmit);
        }) : onPrimarySubmit;
        if (modalProps.hideSecondaryButton) {
            propsToUse.secondaryButtonText = undefined;
            propsToUse.onSecondarySubmit = undefined;
        }
        if (disableListener && typeof disableListener.pipe === 'function') {
            propsToUse.primaryButtonDisabled = true;
            subId = this.subManager.setSubscription(disableListener.subscribe({
                next: disabled => {
                    propsToUse.primaryButtonDisabled = disabled;
                    this.setModalContent(id, propsToUse, modalBody);
                },
                error: error => {
                    new AsperaError('ModalService: Unable to subscribe to disable listener', error);
                },
            }));
        }
        return propsToUse;
    }
    /**
     * Get the ID and componentWillUnmount function to use for data exchange.
     *
     * @remarks Even if data is not needed you should use the componentWillUnmount to clean up subscriptions in memory
     *
     * @returns an object with id (pass this to `create` and a callback).  Both success and error return type `ModalServiceResponse`
     *
     * @example
     * ```javascript
     * const modalData = modal.prepare();
     * MyComponent {
     *    componentWillUnmount(): void {
     *      const { modalClose } = this.props;
     *      modalClose(this.usefulData);
     *    }
     * }
     * const component = <MyComponent modalClose={modalData.callback} prop1="test" />;
     * modal.create(modalData.id, {modalHeading: 'My Modal'}, component).subscribe({
     *   next: data => {
     *     print('Data from modal primary submit', data);
     *   },
     *   error: close => {
     *     print('Modal was closed via X or secondary', close.secondaryClose, close.xClose);
         },
     * });
     *
     * ```
     */
    prepare() {
        const id = getRandomId();
        const callback = (data) => {
            const subject = this.modalSubjects.get(id);
            const reason = this.modalStatus.get(id);
            if (subject && !subject.closed && reason) {
                reason.data = data;
                if (reason.primaryClose) {
                    subject.next(reason);
                }
                else {
                    subject.error(reason);
                }
                subject.complete();
                this.modalSubjects.delete(id);
                this.modalStatus.delete(id);
            }
        };
        return { id, callback };
    }
    /**
     * Create a modal to show on the DOM.  You must call prepare prior to create. That provides the ID and the callback for componentWillUnmount
     *
     * @param id - the ID of the modal
     * @param modalProps - limited props that can used for AsperaModals. Most common defaults are applied to simplify this
     * @param modalBody - the body content of the modal @example `<MyModal modalClose={modalData.callback} prop1="test" />`
     * @param disableListener - observable that takes a boolean indicating if the modal primary action should be disabled or not; if undefined the modal is never disabled.  This will result in the modal starting off disabled, follow with a next(true) to start it off enabled.
     * @param disableOverflowProperty - indicate if the overflow should be turned off for this modal. WARNING: Only use this for very short modals. As this will allow the modal to break out on small height or too long of forms.
     *
     * @returns on observable that goes into success if OK used or error if X or secondary used
     */
    create(id, modalProps, modalBody, disableListener, disableOverflowProperty) {
        const subject = new Subject();
        this.modalSubjects.set(id, subject);
        this.setModalContent(id, this.getPropsToUse(id, modalProps, modalBody, disableListener, disableOverflowProperty), modalBody);
        return subject.asObservable();
    }
    /**
     * Update a modal's props on the DOM
     *
     * @param id - the ID of the modal
     * @param modalProps - limited props that can used for AsperaModals. Most common defaults are applied to simplify this
     * @param modalBody - the body content of the modal @example `<MyModal modalClose={modalData.callback} prop1="test" />`
     * @param disableListener - observable that takes a boolean indicating if the modal primary action should be disabled or not; if undefined the modal is never disabled.  This will result in the modal starting off disabled, follow with a next(true) to start it off enabled.
     * @param disableOverflowProperty - indicate if the overflow should be turned off for this modal. WARNING: Only use this for very short modals. As this will allow the modal to break out on small height or too long of forms.
     *
     * @returns on observable that goes into success if OK used or error if X or secondary used
     */
    update(id, modalProps, modalBody, disableListener, disableOverflowProperty) {
        this.setModalContent(id, this.getPropsToUse(id, modalProps, modalBody, disableListener, disableOverflowProperty), modalBody);
    }
    /**
     * Trigger a repeat of Form focus on a modal. This is useful for modals that
     * have initial state as loading.
     *
     * Be sure to do it via the callback on setState.
     */
    triggerFormFocus() {
        this.formFocus.next(true);
    }
    /**
     * Returns an observable that tells AsperaModalFocusTrap to retry focus trap
     */
    get formFocusTrigger() {
        return this.formFocus.asObservable();
    }
    /**
     * Allow components to subscribe to watch for modal updates and know to update state
     *
     * @remarks should only be used by the app root
     *
     * @returns an observable that can be subscribed to
     */
    get modalNotifier() {
        return this.notifier.asObservable();
    }
    /**
     * Returns an array of Modals (ReactNode) to render on the UI
     *
     * @returns an array of ReactNodes to render
     */
    get modalsToRender() {
        return iterableToArray(this.modals.values());
    }
}
/** The shared modal service */
export const modal = new ModalService();
